import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';

class AuthService {
  constructor() {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.setCustomParameters({
      prompt: 'select_account'
    });
  }

  // Authentication methods
  async signInWithEmail(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await this.updateLastActive(result.user.uid);
      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error.message };
    }
  }

  async signUpWithEmail(email, password, displayName) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update user profile
      await updateProfile(result.user, {
        displayName: displayName
      });

      // Send verification email FIRST — before Firestore writes
      try {
        await sendEmailVerification(result.user);
        console.log('Verification email sent');
      } catch (verifError) {
        console.error('Failed to send verification email:', verifError);
      }

      // Check for referral code
      const referralCode = localStorage.getItem('ct_referral_code');

      // Create user document in Firestore (non-blocking — don't fail signup if this errors)
      try {
        await this.createUserProfile(result.user, { displayName, referredBy: referralCode || null });
      } catch (profileError) {
        console.warn('Profile creation will retry on next login:', profileError.message);
      }

      // Clear the referral code after use
      if (referralCode) localStorage.removeItem('ct_referral_code');

      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error.message };
    }
  }

  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, this.googleProvider);

      // Check for referral code (for new users signing up via Google)
      const referralCode = localStorage.getItem('ct_referral_code');

      // Create or update user profile
      await this.createUserProfile(result.user, { referredBy: referralCode || null });
      await this.updateLastActive(result.user.uid);

      // Clear the referral code after use
      if (referralCode) localStorage.removeItem('ct_referral_code');

      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error.message };
    }
  }

  async signOut() {
    try {
      await signOut(auth);
      // Clear all client-side stored data on logout for security
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.clear();
          window.sessionStorage.clear();
        }
      } catch (e) {
        console.warn('Error clearing storage on sign out', e);
      }
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  }

  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  }

  async updateUserPassword(newPassword) {
    try {
      await updatePassword(auth.currentUser, newPassword);
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  }

  // User profile management
  async createUserProfile(user, additionalData = {}) {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      const { displayName, email, photoURL } = user;
      const referredBy = additionalData.referredBy || null;

      try {
        await setDoc(userRef, {
          displayName: displayName || additionalData.displayName || '',
          email,
          photoURL: photoURL || '',
          createdAt: serverTimestamp(),
          lastActive: serverTimestamp(),
          onboardingCompleted: false,
          newsletter: true, // Auto-subscribe to newsletter
          referredBy: referredBy, // Track who referred this user
          subscription: {
            plan: 'free',
            status: 'active',
            expiresAt: null
          },
          preferences: {
            notifications: true,
            emailUpdates: true,
            theme: 'dark'
          },
          stats: {
            roadmapsGenerated: 0,
            tasksCompleted: 0,
            weeksCompleted: 0,
            currentStreak: 0,
            longestStreak: 0
          },
          ...additionalData
        });

        // Complete referral reward: call backend to grant Premium to both parties
        if (referredBy) {
          try {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const token = await user.getIdToken();
            await fetch(`${API_BASE_URL}/api/features/referral/complete`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ referral_code: referredBy, referred_user_id: user.uid })
            });
            console.log('Referral reward completed for code:', referredBy);
          } catch (referralError) {
            console.warn('Failed to complete referral reward:', referralError);
          }
        }

        // Auto-subscribe to newsletter via API
        if (email) {
          try {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            await fetch(`${API_BASE_URL}/api/newsletter/subscribe`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, source: 'signup' })
            });
            console.log('User auto-subscribed to newsletter');
          } catch (newsletterError) {
            console.warn('Failed to auto-subscribe to newsletter:', newsletterError);
          }
        }
      } catch (error) {
        console.error('Error creating user profile:', error);
      }
    }
  }

  async updateUserProfile(userId, data) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  }

  async getUserProfile(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        return { profile: { id: userId, ...userDoc.data() }, error: null };
      } else {
        return { profile: null, error: 'User profile not found' };
      }
    } catch (error) {
      return { profile: null, error: error.message };
    }
  }

  async updateLastActive(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastActive: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating last active:', error);
    }
  }

  // Resend verification email
  async resendVerificationEmail() {
    try {
      const user = auth.currentUser;
      if (!user) return { error: 'No user logged in' };
      if (user.emailVerified) return { error: 'Email already verified' };
      await sendEmailVerification(user);
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  }

  // Check if current user's email is verified
  isEmailVerified() {
    const user = auth.currentUser;
    if (!user) return false;
    // Google users are auto-verified
    if (user.providerData?.some(p => p.providerId === 'google.com')) return true;
    return user.emailVerified;
  }

  // Auth state listener
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  }

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!auth.currentUser;
  }
}

// Create and export the instance
const authServiceInstance = new AuthService();
export default authServiceInstance; 